import { useEffect, useMemo, useRef, useState } from "react";
import { Undo2, RotateCcw, Trophy, Lightbulb, X, Check, Share2, MessageCircle, Play, Flame, XCircle, Send } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { useLanguage } from "@/i18n/LanguageContext";

const ACCENT = "#8B7CFF";
const TARGET_YELLOW = "#FFC531";

const fmtTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

const OPS = [
  { id: "add", sym: "+", fn: (a, b) => a + b },
  { id: "sub", sym: "−", fn: (a, b) => a - b },
  { id: "mul", sym: "×", fn: (a, b) => a * b },
  { id: "div", sym: "÷", fn: (a, b) => (b !== 0 && a % b === 0 ? a / b : null) },
];

// PRNG deterministico: stesso puzzle per tutti i visitatori nello stesso giorno
const mulberry32 = (seed) => () => {
  seed |= 0;
  seed = (seed + 0x6d2b79f5) | 0;
  let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

// Costruisce il puzzle "a ritroso" dal bersaglio: ogni tessera nasce
// invertendo un'operazione, quindi la soluzione esiste sempre
const buildDailyPuzzle = (date) => {
  const seed = date.getFullYear() * 10000 + (date.getMonth() + 1) * 100 + date.getDate();
  const rnd = mulberry32(seed);
  const target = 25 + Math.floor(rnd() * 126); // 25–150
  let tiles = [target];
  let guard = 0;
  while (tiles.length < 6 && guard++ < 400) {
    const idx = Math.floor(rnd() * tiles.length);
    const n = tiles[idx];
    const roll = rnd();
    let pair = null;
    if (roll < 0.3 && n >= 4) {
      const lo = rnd() < 0.12 ? 1 : 2; // solo di rado tessere da 1
      const a = lo + Math.floor(rnd() * (n - lo - 1));
      pair = [a, n - a]; // a + b = n
    } else if (roll < 0.55) {
      const divs = [];
      for (let dv = 2; dv <= Math.sqrt(n); dv++) if (n % dv === 0) divs.push(dv);
      if (divs.length > 0) {
        const dv = divs[Math.floor(rnd() * divs.length)];
        pair = [dv, n / dv]; // a × b = n
      }
    } else if (roll < 0.8) {
      const b = 3 + Math.floor(rnd() * 23);
      pair = [n + b, b]; // a − b = n
    } else if (n <= 20) {
      const b = 2 + Math.floor(rnd() * 8);
      pair = [n * b, b]; // a ÷ b = n
    }
    if (!pair) {
      if (n >= 4) {
        const a = 2 + Math.floor(rnd() * (n - 3));
        pair = [a, n - a];
      } else {
        continue;
      }
    }
    tiles.splice(idx, 1, pair[0], pair[1]);
  }
  for (let i = tiles.length - 1; i > 0; i--) {
    const j = Math.floor(rnd() * (i + 1));
    [tiles[i], tiles[j]] = [tiles[j], tiles[i]];
  }
  return { target, tiles };
};

// Risolutore BFS: trova la prima mossa di una soluzione dallo stato attuale.
// Si vince appena una tessera uguaglia il bersaglio, anche se ne restano altre.
const findHint = (values, target) => {
  if (values.some((v) => v === target)) return null;
  const key = (arr) => [...arr].sort((x, y) => x - y).join(",");
  const seen = new Set([key(values)]);
  const queue = [{ state: values, first: null }];
  while (queue.length) {
    const { state, first } = queue.shift();
    if (state.some((v) => v === target)) return first;
    for (let i = 0; i < state.length; i++) {
      for (let j = 0; j < state.length; j++) {
        if (i === j) continue;
        const a = state[i];
        const b = state[j];
        for (const op of OPS) {
          const r = op.fn(a, b);
          if (r === null) continue;
          const rest = state.filter((_, k) => k !== i && k !== j);
          const ns = [...rest, r];
          const k = key(ns);
          if (seen.has(k)) continue;
          seen.add(k);
          const move = { a, sym: op.sym, b };
          queue.push({ state: ns, first: first || move });
        }
      }
    }
  }
  return null;
};

const freshTiles = (puzzle, startId) => puzzle.tiles.map((v, i) => ({ id: startId + i, value: v }));

const CifrooDemo = () => {
  const { t, lang } = useLanguage();
  const d = t.demo;
  const idCounter = useRef(100);
  const dailyPuzzle = useMemo(() => buildDailyPuzzle(new Date()), []);
  const dateKey = useMemo(() => {
    const n = new Date();
    return `${n.getFullYear()}${String(n.getMonth() + 1).padStart(2, "0")}${String(n.getDate()).padStart(2, "0")}`;
  }, []);
  const dailyDate = useMemo(
    () =>
      new Date().toLocaleDateString(lang === "it" ? "it-IT" : "en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      }),
    [lang]
  );
  const [tiles, setTiles] = useState(() => freshTiles(dailyPuzzle, 0));
  const [selTile, setSelTile] = useState(null);
  const [selOp, setSelOp] = useState(null);
  const [ready, setReady] = useState(false); // bersaglio raggiunto, in attesa di conferma
  const [history, setHistory] = useState([]);
  const [won, setWon] = useState(false);
  const [lost, setLost] = useState(false);
  const [winDismissed, setWinDismissed] = useState(false);
  const [error, setError] = useState(null);
  const [hint, setHint] = useState(null);
  const [moves, setMoves] = useState([]);
  const [copied, setCopied] = useState(false);
  const [started, setStarted] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [finalTime, setFinalTime] = useState(null);
  const [streak, setStreak] = useState(
    () => typeof window !== "undefined" && Number(localStorage.getItem("cifroo-streak") || 0)
  );
  const startRef = useRef(null);

  // Cronometro: parte dal pulsante "Inizia la sfida" e si ferma alla vittoria
  useEffect(() => {
    if (!started || won) return undefined;
    const iv = setInterval(() => setElapsed(Math.floor((Date.now() - startRef.current) / 1000)), 1000);
    return () => clearInterval(iv);
  }, [started, won]);

  const handleStart = () => {
    setStarted(true);
    startRef.current = Date.now();
    setElapsed(0);
  };
  const [hintUsed, setHintUsed] = useState(
    () => typeof window !== "undefined" && localStorage.getItem(`cifroo-hint-${dateKey}`) === "1"
  );
  const errorTimer = useRef(null);

  const flashError = (msg) => {
    setError(msg);
    clearTimeout(errorTimer.current);
    errorTimer.current = setTimeout(() => setError(null), 1600);
  };

  const clearSelection = () => {
    setSelTile(null);
    setSelOp(null);
  };

  const resetTiles = () => {
    setTiles(freshTiles(dailyPuzzle, idCounter.current));
    idCounter.current += dailyPuzzle.tiles.length;
    setHistory([]);
    clearSelection();
    setHint(null);
    setMoves([]);
    setReady(false);
    setLost(false);
  };

  const handleTileClick = (tile) => {
    if (won || ready || lost) return;
    if (selTile === null) {
      setSelTile(tile.id);
      return;
    }
    if (selTile === tile.id) {
      clearSelection();
      return;
    }
    if (!selOp) {
      setSelTile(tile.id);
      return;
    }
    const a = tiles.find((x) => x.id === selTile);
    const op = OPS.find((o) => o.id === selOp);
    const result = op.fn(a.value, tile.value);
    if (result === null) {
      flashError(d.divError);
      return;
    }
    const merged = { id: idCounter.current++, value: result };
    const next = [...tiles.filter((x) => x.id !== selTile && x.id !== tile.id), merged];
    setHistory((h) => [...h, tiles]);
    setTiles(next);
    setMoves((m) => [...m, op.id]);
    clearSelection();
    if (next.some((x) => x.value === dailyPuzzle.target)) {
      setReady(true);
    } else if (!findHint(next.map((x) => x.value), dailyPuzzle.target)) {
      setLost(true);
    }
  };

  const handleOpClick = (opId) => {
    if (won || ready || lost || selTile === null) return;
    setSelOp(selOp === opId ? null : opId);
  };

  const handleConfirm = () => {
    if (won || !ready) return;
    setWon(true);
    setWinDismissed(false);
    setFinalTime(elapsed);
    // Serie giornaliera: +1 se ieri completata, altrimenti riparte da 1
    const last = localStorage.getItem("cifroo-streak-last");
    if (last !== dateKey) {
      const y = new Date();
      y.setDate(y.getDate() - 1);
      const yKey = `${y.getFullYear()}${String(y.getMonth() + 1).padStart(2, "0")}${String(y.getDate()).padStart(2, "0")}`;
      const s = last === yKey ? streak + 1 : 1;
      setStreak(s);
      localStorage.setItem("cifroo-streak", String(s));
      localStorage.setItem("cifroo-streak-last", dateKey);
    }
  };

  const handleUndo = () => {
    if (won || history.length === 0) return;
    setTiles(history[history.length - 1]);
    setHistory((h) => h.slice(0, -1));
    setMoves((m) => m.slice(0, -1));
    clearSelection();
    setReady(false);
    setLost(false);
  };

  const handleRestart = () => {
    if (won) return;
    resetTiles();
  };

  const handleHint = () => {
    if (won || hintUsed) return;
    const move = findHint(tiles.map((x) => x.value), dailyPuzzle.target);
    if (!move) return;
    setHint(move);
    setHintUsed(true);
    localStorage.setItem(`cifroo-hint-${dateKey}`, "1");
  };

  const selTileValue = tiles.find((x) => x.id === selTile)?.value;
  const selOpSym = OPS.find((o) => o.id === selOp)?.sym;

  // Testo di condivisione stile Wordle: emoji per ogni mossa confermata
  const OP_EMOJI = { add: "🟩", sub: "🟦", mul: "🟨", div: "🟪" };
  const shareText = `Cifroo · ${d.title} · ${dailyDate}\n🎯 ${dailyPuzzle.target}\n${moves.map((m) => OP_EMOJI[m]).join("")} (${moves.length})\n⏱ ${fmtTime(finalTime ?? elapsed)}\nhttps://fabiocomelli.it/apps/cifroo`;

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ text: shareText });
        return;
      } catch (e) {
        /* condivisione annullata: niente fallback */
      }
    }
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (e) {
      /* clipboard non disponibile */
    }
  };

  return (
    <section className="mt-24" data-testid="cifroo-demo">
      <Reveal>
        <p className="mb-4 font-mono text-xs uppercase tracking-[0.3em]" style={{ color: ACCENT }}>
          {d.kicker}
        </p>
        <h2 className="font-display text-3xl font-bold uppercase tracking-tight md:text-4xl">{d.title}</h2>
        <p className="mt-4 max-w-xl text-sm leading-relaxed text-muted2 md:text-base">{d.sub}</p>
      </Reveal>

      <Reveal delay={0.1}>
        <div
          className="relative mt-10 overflow-hidden rounded-3xl border p-6 md:p-10"
          style={{ borderColor: `${ACCENT}45`, backgroundColor: `${ACCENT}0A` }}
        >
          {/* Data, serie e cronometro */}
          <div className="flex items-center justify-between">
            <p className="font-mono text-xs uppercase tracking-widest text-muted2" data-testid="demo-date">
              {dailyDate}
            </p>
            <div className="flex items-center gap-4 font-mono text-xs uppercase tracking-widest text-muted2">
              <span className="inline-flex items-center gap-1.5" data-testid="demo-streak" title={d.streakLabel}>
                <Flame className="h-4 w-4" style={{ color: TARGET_YELLOW }} />
                {streak}
              </span>
              {started && (
                <span className="tabular-nums" data-testid="demo-timer">
                  {fmtTime(won ? (finalTime ?? elapsed) : elapsed)}
                </span>
              )}
            </div>
          </div>

          {/* Bersaglio */}
          <div className="mt-6 flex items-center justify-between rounded-2xl border border-white/10 bg-white/[0.04] px-6 py-5">
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-muted2">{d.target}</span>
            <span
              className="font-display text-4xl font-extrabold md:text-5xl"
              style={{ color: TARGET_YELLOW }}
              data-testid="demo-target"
            >
              {dailyPuzzle.target}
            </span>
          </div>

          {/* Espressione + conferma finale */}
          <div
            className="mt-6 flex h-12 items-center justify-between gap-3 rounded-xl border px-4 transition-colors duration-300"
            style={{
              borderColor: ready ? `${TARGET_YELLOW}88` : "rgba(255,255,255,0.1)",
              backgroundColor: ready ? `${TARGET_YELLOW}0D` : "rgba(255,255,255,0.03)",
            }}
            data-testid="demo-expression"
          >
            <span className="font-mono text-lg" style={ready ? { color: TARGET_YELLOW } : undefined}>
              {ready
                ? d.readyText
                : selTileValue !== undefined
                  ? `${selTileValue} ${selOpSym || "?"} ?`
                  : "—"}
            </span>
            {ready && (
              <button
                onClick={handleConfirm}
                data-testid="demo-confirm"
                className="inline-flex shrink-0 items-center gap-2 rounded-full px-4 py-1.5 font-mono text-[11px] uppercase tracking-widest text-black transition-transform duration-200 hover:scale-105"
                style={{ backgroundColor: TARGET_YELLOW }}
              >
                <Check className="h-4 w-4" />
                {d.confirm}
              </button>
            )}
          </div>

          {/* Tessere */}
          <div className="mt-6 flex flex-wrap items-center justify-center gap-4">
            {tiles.map((tile) => {
              const active = selTile === tile.id;
              return (
                <button
                  key={tile.id}
                  onClick={() => handleTileClick(tile)}
                  data-testid={`demo-tile-${tile.id}`}
                  className="h-16 w-16 rounded-2xl border bg-surface font-display text-2xl font-bold transition-all duration-200 hover:-translate-y-1 md:h-20 md:w-20 md:text-3xl"
                  style={{
                    borderColor: active ? ACCENT : "rgba(255,255,255,0.15)",
                    boxShadow: active ? `0 0 24px ${ACCENT}55` : "none",
                    transform: active ? "scale(1.08)" : undefined,
                    color: active ? ACCENT : "white",
                  }}
                >
                  {tile.value}
                </button>
              );
            })}
          </div>

          {/* Operazioni */}
          <div className="mt-6 grid grid-cols-4 gap-3">
            {OPS.map((op) => (
              <button
                key={op.id}
                onClick={() => handleOpClick(op.id)}
                data-testid={`demo-op-${op.id}`}
                disabled={selTile === null}
                className="rounded-xl border py-3 font-display text-2xl font-bold transition-all duration-200 enabled:hover:-translate-y-0.5 disabled:opacity-30"
                style={{
                  borderColor: selOp === op.id ? TARGET_YELLOW : "rgba(255,255,255,0.15)",
                  backgroundColor: selOp === op.id ? `${TARGET_YELLOW}22` : "rgba(255,255,255,0.03)",
                  color: selOp === op.id ? TARGET_YELLOW : "white",
                }}
              >
                {op.sym}
              </button>
            ))}
          </div>

          {/* Messaggi: errore o suggerimento */}
          <p
            className="mt-4 h-5 text-center font-mono text-xs transition-opacity duration-300"
            style={{ opacity: error || hint ? 1 : 0, color: error ? "#f87171" : TARGET_YELLOW }}
            data-testid="demo-message"
          >
            {error || (hint ? `${d.hintPrefix}: ${hint.a} ${hint.sym} ${hint.b}` : "·")}
          </p>

          {/* Annulla / Aiuto / Ricomincia */}
          <div className="mt-2 grid grid-cols-3 gap-3">
            <button
              onClick={handleUndo}
              disabled={history.length === 0}
              data-testid="demo-undo"
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/15 bg-white/[0.03] py-3 font-mono text-xs uppercase tracking-widest transition-colors duration-300 enabled:hover:border-white/40 disabled:opacity-30"
            >
              <Undo2 className="h-4 w-4" />
              {d.undo}
            </button>
            <button
              onClick={handleHint}
              disabled={hintUsed}
              data-testid="demo-hint"
              className="inline-flex items-center justify-center gap-2 rounded-xl border py-3 font-mono text-xs uppercase tracking-widest transition-colors duration-300 disabled:opacity-30"
              style={{
                borderColor: `${TARGET_YELLOW}66`,
                backgroundColor: `${TARGET_YELLOW}11`,
                color: TARGET_YELLOW,
              }}
            >
              <Lightbulb className="h-4 w-4" />
              {d.hint}
            </button>
            <button
              onClick={handleRestart}
              data-testid="demo-restart"
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/15 bg-white/[0.03] py-3 font-mono text-xs uppercase tracking-widest transition-colors duration-300 hover:border-white/40"
            >
              <RotateCcw className="h-4 w-4" />
              {d.restart}
            </button>
          </div>

          {/* Overlay iniziale: i numeri si scoprono al via */}
          {!started && (
            <div
              className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-5 bg-void/85 backdrop-blur-md"
              data-testid="demo-start-overlay"
            >
              <p className="px-6 text-center font-mono text-xs uppercase tracking-widest text-muted2">
                {d.title} · {dailyDate}
              </p>
              <button
                onClick={handleStart}
                data-testid="demo-start"
                className="inline-flex items-center gap-2 rounded-full px-8 py-3 font-mono text-xs uppercase tracking-widest text-black transition-transform duration-200 hover:scale-105"
                style={{ backgroundColor: TARGET_YELLOW }}
              >
                <Play className="h-4 w-4" />
                {d.start}
              </button>
            </div>
          )}

          {/* Overlay sconfitta */}
          {lost && !ready && (
            <div
              className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-5 bg-void/85 backdrop-blur-md"
              data-testid="demo-lost"
            >
              <XCircle className="h-12 w-12 text-red-400" />
              <p className="px-6 text-center font-display text-2xl font-bold uppercase tracking-tight md:text-3xl">
                {d.lostTitle}
              </p>
              <p className="max-w-md px-6 text-center text-sm leading-relaxed text-muted2">{d.lostSub}</p>
              <div className="flex flex-wrap items-center justify-center gap-3">
                {history.length > 0 && (
                  <button
                    onClick={handleUndo}
                    data-testid="demo-lost-undo"
                    className="inline-flex items-center gap-2 rounded-full border border-white/15 px-6 py-2.5 font-mono text-xs uppercase tracking-widest transition-colors duration-300 hover:border-white/40"
                  >
                    <Undo2 className="h-4 w-4" />
                    {d.undo}
                  </button>
                )}
                <button
                  onClick={handleRestart}
                  data-testid="demo-lost-retry"
                  className="inline-flex items-center gap-2 rounded-full px-6 py-2.5 font-mono text-xs uppercase tracking-widest text-black transition-transform duration-200 hover:scale-105"
                  style={{ backgroundColor: TARGET_YELLOW }}
                >
                  <RotateCcw className="h-4 w-4" />
                  {d.retry}
                </button>
              </div>
            </div>
          )}

          {/* Overlay vittoria */}
          {won && !winDismissed && (
            <div
              className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-5 bg-void/85 backdrop-blur-md"
              data-testid="demo-win"
            >
              <button
                onClick={() => setWinDismissed(true)}
                data-testid="demo-win-close"
                className="absolute top-5 right-5 rounded-full border border-white/15 p-2 text-muted2 transition-colors duration-300 hover:border-white/40 hover:text-white"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
              <Trophy className="h-12 w-12" style={{ color: TARGET_YELLOW }} />
              <p className="px-6 text-center font-display text-2xl font-bold uppercase tracking-tight md:text-3xl">
                {d.dailyWin}
              </p>
              <p className="max-w-md px-6 text-center font-mono text-sm leading-relaxed tracking-widest" data-testid="demo-win-moves">
                {moves.map((m) => OP_EMOJI[m]).join(" ")}
              </p>
              <p className="font-mono text-xs uppercase tracking-widest" style={{ color: TARGET_YELLOW }} data-testid="demo-win-stats">
                {d.solvedIn} {fmtTime(finalTime ?? 0)} · {d.streakLabel}: {streak} {streak === 1 ? d.dayOne : d.dayMany}
              </p>
              <p className="max-w-md px-6 text-center text-sm leading-relaxed text-muted2">{d.dailyWinSub}</p>
              <div className="flex flex-wrap items-center justify-center gap-3">
                <button
                  onClick={handleShare}
                  data-testid="demo-share"
                  className="inline-flex items-center gap-2 rounded-full px-6 py-2.5 font-mono text-xs uppercase tracking-widest text-black transition-transform duration-200 hover:scale-105"
                  style={{ backgroundColor: TARGET_YELLOW }}
                >
                  <Share2 className="h-4 w-4" />
                  {copied ? d.copied : d.share}
                </button>
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(shareText)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="demo-share-whatsapp"
                  className="inline-flex items-center gap-2 rounded-full border border-[#25D366]/50 px-6 py-2.5 font-mono text-xs uppercase tracking-widest text-[#25D366] transition-colors duration-300 hover:bg-[#25D366]/10"
                >
                  <MessageCircle className="h-4 w-4" />
                  WhatsApp
                </a>
                <a
                  href={`https://t.me/share/url?url=${encodeURIComponent("https://fabiocomelli.it/apps/cifroo")}&text=${encodeURIComponent(shareText)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="demo-share-telegram"
                  className="inline-flex items-center gap-2 rounded-full border border-[#2AABEE]/50 px-6 py-2.5 font-mono text-xs uppercase tracking-widest text-[#2AABEE] transition-colors duration-300 hover:bg-[#2AABEE]/10"
                >
                  <Send className="h-4 w-4" />
                  Telegram
                </a>
              </div>
            </div>
          )}
        </div>
      </Reveal>
    </section>
  );
};

export default CifrooDemo;
